import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { DEFAULT_CONTENT, cloneValue } from '../content/siteContent';
import { getStorageMode, API_BASE } from '../utils/storageMode';
import { uploadImageFile } from '../utils/uploadImage';

const ContentContext = createContext(null);

const STORAGE_KEY = 'knitster_content';

/**
 * Merge saved overrides on top of the defaults.
 * Only keys that exist in the defaults are merged, so removing a field from the
 * schema automatically drops any stale saved value.
 */
const mergeContent = (overrides) => {
    const merged = cloneValue(DEFAULT_CONTENT);

    if (!overrides || typeof overrides !== 'object') return merged;

    Object.keys(merged).forEach(pageKey => {
        const pageOverride = overrides[pageKey];
        if (!pageOverride) return;

        Object.keys(merged[pageKey]).forEach(groupKey => {
            const groupOverride = pageOverride[groupKey];
            if (!groupOverride) return;

            Object.keys(merged[pageKey][groupKey]).forEach(fieldKey => {
                const saved = groupOverride[fieldKey];
                if (saved === undefined || saved === null) return;

                // Ignore a saved value whose shape no longer matches the schema,
                // so a page that expects a list can never be handed a string.
                if (Array.isArray(merged[pageKey][groupKey][fieldKey]) !== Array.isArray(saved)) return;

                merged[pageKey][groupKey][fieldKey] = saved;
            });
        });
    });

    return merged;
};

export const ContentProvider = ({ children }) => {
    const [content, setContent] = useState(() => cloneValue(DEFAULT_CONTENT));
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [storageMode] = useState(getStorageMode);

    useEffect(() => {
        const loadContent = async () => {
            setIsLoading(true);
            setError(null);

            try {
                if (storageMode === 'api') {
                    const response = await fetch(`${API_BASE}/content.php`, {
                        credentials: 'include'
                    });
                    const data = await response.json();

                    if (data.success) {
                        setContent(mergeContent(data.content));
                    } else {
                        throw new Error(data.error || 'Failed to load content');
                    }
                } else {
                    const saved = localStorage.getItem(STORAGE_KEY);
                    setContent(mergeContent(saved ? JSON.parse(saved) : null));
                }
            } catch (err) {
                console.error('Error loading site content:', err);
                setError(err.message);
                // The site still renders with the built-in defaults.
                setContent(cloneValue(DEFAULT_CONTENT));
            } finally {
                setIsLoading(false);
            }
        };

        loadContent();
    }, [storageMode]);

    /** Persist the whole content tree (admin only). */
    const saveContent = useCallback(async (nextContent) => {
        const merged = mergeContent(nextContent);

        if (storageMode === 'api') {
            const response = await fetch(`${API_BASE}/content.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(merged)
            });

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Failed to save content');
            }
            setContent(mergeContent(data.content));
        } else {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
            setContent(merged);
        }
    }, [storageMode]);

    /** Throw away every edit and go back to the built-in copy. */
    const resetContent = useCallback(async () => {
        if (storageMode === 'api') {
            const response = await fetch(`${API_BASE}/content.php?action=reset`, {
                method: 'POST',
                credentials: 'include'
            });
            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Failed to reset content');
            }
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
        setContent(cloneValue(DEFAULT_CONTENT));
    }, [storageMode]);

    const uploadImage = useCallback((file) => uploadImageFile(file, storageMode), [storageMode]);

    const value = useMemo(() => ({
        content,
        isLoading,
        error,
        storageMode,
        saveContent,
        resetContent,
        uploadImage
    }), [content, isLoading, error, storageMode, saveContent, resetContent, uploadImage]);

    return (
        <ContentContext.Provider value={value}>
            {children}
        </ContentContext.Provider>
    );
};

export const useContentStore = () => {
    const context = useContext(ContentContext);
    if (!context) {
        throw new Error('useContentStore must be used within a ContentProvider');
    }
    return context;
};

/**
 * Page-level accessor.
 *
 *   const { t, g } = usePageContent('ourValues');
 *   t('hero.title')      -> this page's copy
 *   g('contact.email')   -> site-wide copy
 *
 * Missing keys return '' (or [] where a list was expected) so a typo can never
 * blank out the page or throw.
 */
export const usePageContent = (pageKey) => {
    const { content } = useContentStore();

    return useMemo(() => {
        const read = (page, path) => {
            const [groupKey, fieldKey] = String(path).split('.');
            const group = content[page] && content[page][groupKey];
            const value = group ? group[fieldKey] : undefined;
            return value === undefined || value === null ? '' : value;
        };

        return {
            t: (path) => read(pageKey, path),
            g: (path) => read('global', path)
        };
    }, [content, pageKey]);
};
